/**
 * Чий `next` (знахідка Security з #408, тікет 24af8f72). Застосунок веде на вхід з `?next=<адреса
 * екрана>` (UX-06), але після входу туди має потрапити лише та сама людина. Інакше адреса запису А
 * доходить до Б:
 * - сесія А спливла чи відкликана — вкладка А стоїть на `/login?next=<адреса А>`, і Б входить саме
 *   там;
 * - дві вкладки — 401 у вкладці 2 приходить раніше за повідомлення `BroadcastChannel` про вихід А,
 *   і вкладка 2 іде на вхід з власною адресою.
 * Даних сервер Б не віддасть, але адреса чужого запису і перехід туди — витік і несподіванка.
 *
 * Перехід застосунку на вхід кладе поруч із `next` мітку людини (id з `/me`) у `sessionStorage`
 * вкладки: її не підробить посилання і не побачить інша вкладка. Після входу документ входу
 * звіряє, хто тепер у cookie (`GET /api/v1/me`): інша людина чи відповіді немає — на старт.
 * `next` без мітки (посилання, з яким людина прийшла без входу) діє, як і раніше.
 *
 * Модуль без залежностей: його бере і документ входу (login.html, бюджет ≤ 100 КБ).
 */

const KEY = 'cl:next-owner';

interface Stored {
  next: string;
  user: string;
}

/**
 * Застосунок іде на вхід з `next`: запам'ятати, чий він. Людина невідома (`user` порожній) чи
 * `next` немає — мітку прибрати, щоб стара не прив'язала чужий `next`.
 */
export function bindNext(next: string | null, user: string | null | undefined): void {
  try {
    if (next && user) sessionStorage.setItem(KEY, JSON.stringify({ next, user } satisfies Stored));
    else sessionStorage.removeItem(KEY);
  } catch {
    // Сховища немає (приватний режим) — `next` лишається без мітки, як посилання.
  }
}

/**
 * Після входу: id людини, для якої застосунок поклав саме цей `next`, або `null` — `next` не
 * прив'язаний. Мітку прибирає одразу: вона на один вхід.
 */
export function takeNextOwner(next: string): string | null {
  let raw: string | null;
  try {
    raw = sessionStorage.getItem(KEY);
    sessionStorage.removeItem(KEY);
  } catch {
    return null;
  }
  if (!raw) return null;
  try {
    const stored = JSON.parse(raw) as Partial<Stored> | null;
    if (typeof stored?.next !== 'string' || typeof stored.user !== 'string') return null;
    return stored.next === next ? stored.user : null;
  } catch {
    return null;
  }
}

/**
 * Куди після входу: `next`, якщо він не прив'язаний або прив'язаний до того, хто ввійшов; інакше
 * `start`. Хто ввійшов, каже лише сервер (`me`); збій запиту — на старт, а не на чужу адресу.
 */
export async function nextForSignedIn(
  next: string | null,
  start: string,
  me: () => Promise<{ id: string }>,
): Promise<string> {
  // Мітку забираємо й тоді, коли `next` немає: стара не має дожити до наступного входу.
  const owner = takeNextOwner(next ?? '');
  if (!next) return start;
  if (owner === null) return next;
  try {
    return (await me()).id === owner ? next : start;
  } catch {
    return start;
  }
}
